import { useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Avatar,
  useMediaQuery,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, Link as RouterLink, Navigate } from 'react-router-dom';
import { useGetTransactionTimeDifferenceQuery, TransactionDetail, useGetAmcListQuery } from '@/services/dashboardApi';
import { useGetAggregatorsQuery } from '@/services';
import { useSelector } from 'react-redux';
import { UserRole } from '@/types/user';
import { RootState } from '@/store/store';

export default function Reports() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const role = useSelector((state: RootState) => state.user.role) as UserRole | undefined;

  const isAuthorized = role === UserRole.AMC_ADMIN || role === UserRole.ADMIN || role === UserRole.SUPPORT;
  if (!isAuthorized) {
    return (<Navigate to="/dashboard" />
    );
  }

  const [selectedDate, setSelectedDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('All');
  const [amcFilter, setAmcFilter] = useState('all');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('All');
  const [gatewayFilter, setGatewayFilter] = useState('All');
  const [acknowledgedByFilter, setAcknowledgedByFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch AMC data for filter options
  const { data: amcs = [], isLoading: isLoadingAmcs } = useGetAmcListQuery();

  // Fetch dynamic aggregators based on selected AMC
  const { data: aggregatorsData } = useGetAggregatorsQuery(
    amcFilter && amcFilter !== 'all' ? amcFilter : undefined
  );

  // Build API parameters, only include non-empty filters
  const apiParams: any = {};

  // Add date filter only if selected
  if (selectedDate) {
    apiParams.date = selectedDate;
  }

  if (amcFilter && amcFilter !== 'all') {
    apiParams.amc = amcFilter;
  }
  if (transactionTypeFilter && transactionTypeFilter !== 'All') {
    apiParams.transactionType = transactionTypeFilter;
  }
  if (gatewayFilter && gatewayFilter !== 'All') {
    apiParams.paymentAggregator = gatewayFilter;
  }
  if (statusFilter && statusFilter !== 'All') {
    apiParams.status = statusFilter.toLowerCase();
  }
  if (acknowledgedByFilter && acknowledgedByFilter !== 'All') {
    apiParams.acknowledgedBy = acknowledgedByFilter === 'null' ? null : acknowledgedByFilter;
  }

  // Fetch transaction time difference data with API-supported filters
  const { data: transactionData, isLoading, error } = useGetTransactionTimeDifferenceQuery(apiParams);
  // Process the new API response format
  const processTransactionData = () => {
    if (!transactionData) return [];

    const allTransactions = [
      ...(transactionData.queryApi?.successfulTransactions || []),
      ...(transactionData.queryApi?.failedTransactions || []),
      ...(transactionData.nonQueryApi?.successfulTransactions || []),
      ...(transactionData.nonQueryApi?.failedTransactions || [])
    ];

    return allTransactions;
  };

  const allTransactions = processTransactionData();
  const StatusBadge = ({ status }: { status: string }) => {
    const colors = {
      success: { bg: theme.palette.success.light, color: theme.palette.success.main },
      settled: { bg: theme.palette.success.light, color: theme.palette.success.main },
      pending: { bg: theme.palette.warning.light, color: theme.palette.warning.main },
      failure: { bg: theme.palette.error.light, color: theme.palette.error.main },
      failed: { bg: theme.palette.error.light, color: theme.palette.error.main },
    };
    const normalized = status.toLowerCase();
    const style = colors[(normalized as keyof typeof colors)] || colors.pending;

    return (
      <Box
        sx={{
          backgroundColor: style.bg,
          color: style.color,
          borderRadius: '16px',
          padding: '0px 8px',
          display: 'inline-block',
          fontSize: '14px',
          fontWeight: theme.typography.h1.fontWeight,
          height: '30px',
          lineHeight: '28px',
          textAlign: 'center',
        }}
      >
        {status.toLocaleUpperCase()}
      </Box>
    );
  };


  const handleTransactionClick = (transaction: TransactionDetail) => {
    // Navigate to payment detail page using the transaction ID
    navigate(`/payments/${transaction.id}`);
  };

  // Get all transactions from the API response
  const getAllTransactions = (): TransactionDetail[] => {
    return allTransactions;
  };

  // Apply client-side filters (only for filters not handled by API)
  const getFilteredTransactions = (): TransactionDetail[] => {
    let transactions = getAllTransactions();

    // API handles: amc, transactionType, paymentAggregator, status, date, acknowledgedBy
    // Client-side handles: search

    // Filter by search term (client-side only)
    if (searchTerm) {
      transactions = transactions.filter(t =>
        t.ihNoOrCart.toString().includes(searchTerm) ||
        t.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.gateway.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.transactionType.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by initialized at (gatewayClicked)
    transactions.sort((a, b) => {
      const dateA = new Date(a.gatewayClicked).getTime();
      const dateB = new Date(b.gatewayClicked).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return transactions;
  };

  const filteredTransactions = getFilteredTransactions();
  const paginatedTransactions = filteredTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Get unique gateways from transactions (fallback if API doesn't return data)
  const uniqueGateways = Array.from(new Set(getAllTransactions().map(t => t.gateway)));

  // Use dynamic aggregators if available, otherwise use unique gateways from data
  const gatewayOptions = aggregatorsData?.aggregators
    ? aggregatorsData.aggregators.map(agg => agg.value)
    : uniqueGateways;

  // Filter options (matching PaymentList.tsx)
  const filterOptions = {
    status: ['settled', 'pending', 'failure', 'success'],
    transactionTypes: ['Lumpsum', 'Emandate', 'Hybrid'],
    amcs: amcs,
  };

  return (
    <Box sx={{ height: 'calc(100vh - 140px)', width: '100%', px: isMobile ? 1 : 2 }}>
      <Breadcrumbs
        separator="·"
        sx={{
          mb: 1,
          '& .MuiBreadcrumbs-separator': {
            mx: 1,
            color: theme.palette.text.secondary,
          },
        }}
      >
        <Link
          component={RouterLink}
          to="/dashboard"
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: theme.typography.subtitle1.fontWeight,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Dashboard
        </Link>
        <Typography variant="subtitle1">Reports</Typography>
      </Breadcrumbs>

      {/* Filter and Control Container */}
      <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: '16px', p: 2, mb: 2 }}>
        {/* Filters Row */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
            alignItems: 'flex-start',
          }}
        >
          {/* Status Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {filterOptions.status.map((status) => (
                <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* AMC Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="AMC"
              value={amcFilter}
              onChange={(e) => setAmcFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {filterOptions.amcs.map((amc) => (
                <MenuItem key={amc.id} value={amc.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {amc.logoPath ? (
                      <img
                        src={amc.logoPath}
                        alt=""
                        style={{ width: '20px', height: '20px' }}
                      />
                    ) : (
                      <Avatar sx={{ width: 20, height: 20, fontSize: '12px' }}>
                        {amc.name.charAt(0)}
                      </Avatar>
                    )}
                    {amc.name}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Transaction Type Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Transaction Type"
              value={transactionTypeFilter}
              onChange={(e) => setTransactionTypeFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {filterOptions.transactionTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 16,
                      height: 16,
                      bgcolor: theme.palette.primary.main,
                      borderRadius: '50%'
                    }} />
                    {type}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Payment Aggregator Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Payment Aggregator"
              value={gatewayFilter}
              onChange={(e) => setGatewayFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {gatewayOptions.map((gateway) => (
                <MenuItem key={gateway} value={gateway}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 20, height: 20, bgcolor: '#FF6B35', fontSize: '12px' }}>
                      {gateway.charAt(0)}
                    </Avatar>
                    {gateway}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Acknowledged By Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Acknowledged By"
              value={acknowledgedByFilter}
              onChange={(e) => setAcknowledgedByFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="queryApi">Query API</MenuItem>
              <MenuItem value="callback">Callback</MenuItem>
              <MenuItem value="webhook">Webhook</MenuItem>
              <MenuItem value="StatusCheck">Status Check</MenuItem>
            </TextField>
          </Box>

          {/* Search Field */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 300px', minWidth: 200 }}>
            <TextField
              placeholder="Search Transaction / IH No"
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Date Filter */}
          <Box sx={{ flex: isMobile ? '1 1 100%' : '1 1 200px', minWidth: 200 }}>
            <TextField
              type="date"
              label="Select Date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </Box>

        {/* Reset Filters Button and Pagination */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
          }}
        >
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setStatusFilter('All');
              setAmcFilter('all');
              setTransactionTypeFilter('All');
              setGatewayFilter('All');
              setAcknowledgedByFilter('All');
              setSearchTerm('');
              setSelectedDate('');
            }}
            sx={{
              textTransform: 'none',
              alignSelf: isMobile ? 'flex-start' : 'center',
            }}
          >
            Reset Filters
          </Button>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'center' : 'flex-end',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {Math.min(page * rowsPerPage + 1, filteredTransactions.length)} to{' '}
              {Math.min((page + 1) * rowsPerPage, filteredTransactions.length)} of {filteredTransactions.length} entries
            </Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value as string, 10));
                  setPage(0);
                }}
                displayEmpty
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              per page
            </Typography>

            <Pagination
              count={Math.ceil(filteredTransactions.length / rowsPerPage)}
              page={page + 1}
              onChange={(_event, value) => setPage(value - 1)}
              showFirstButton
              showLastButton
              size="small"
            />
          </Box>
        </Box>
      </Box>

      {/* Data Container */}
      <Box sx={{ backgroundColor: theme.palette.background.paper, borderRadius: '16px', p: 2 }}>
        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="error">
              Error loading transaction data
            </Typography>
          </Box>
        )}

        {/* No Data Available State */}
        {!isLoading && transactionData && getAllTransactions().length === 0 && (
          <Box sx={{ width: '100%', p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No data available
            </Typography>
          </Box>
        )}

        {/* Transaction Table */}
        {!isLoading && transactionData && getAllTransactions().length > 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>AMC / Asset</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Transaction Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Payment Aggregator</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Acknowledged By</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Initialized At</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTransactions.length > 0 ? (
                  paginatedTransactions.map((transaction: TransactionDetail) => (
                    <TableRow
                      key={transaction.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleTransactionClick(transaction)}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 'medium',
                            textDecoration: 'underline',
                            fontSize: '14px'
                          }}
                        >
                          {transaction.ihNoOrCart}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[300] }}>
                            {transaction.amcName?.charAt(0).toUpperCase() || 'N'}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontSize: '14px' }}>{transaction.amcName || 'N/A'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 16,
                            height: 16,
                            bgcolor: theme.palette.primary.main,
                            borderRadius: '50%'
                          }} />
                          <Typography variant="body2" sx={{ fontSize: '14px' }}>{transaction.transactionType}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 20, height: 20, bgcolor: '#FF6B35' }}>
                            B
                          </Avatar>
                          <Typography variant="body2" sx={{ fontSize: '14px' }}>{transaction.gateway}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '14px' }}>
                          {transaction.acknowledgedBy ? transaction.acknowledgedBy : 'None'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '14px' }}>
                          {new Date(transaction.gatewayClicked).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={transaction.status} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: '14px' }}>
                        No transactions found for the selected filters and date range.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
